import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { toast } from 'sonner';
import { 
  Clock, 
  Shield, 
  AlertTriangle,
  Lock,
  Unlock,
  User,
  Key,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function AuditPage() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchLogs = useCallback(async () => {
    try {
      const response = await api.get('/audit?limit=100');
      setLogs(response.data.logs);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionConfig = (action) => {
    const configs = {
      'USER_REGISTERED': { 
        label: 'Account Created', 
        icon: User, 
        color: 'text-[#3B82F6]', 
        bg: 'bg-[#3B82F6]/20',
        category: 'auth'
      },
      'LOGIN_SUCCESS': { 
        label: 'Login Success', 
        icon: Key, 
        color: 'text-[#10B981]', 
        bg: 'bg-[#10B981]/20',
        category: 'auth'
      },
      'LOGIN_FAILED': { 
        label: 'Login Failed', 
        icon: AlertTriangle, 
        color: 'text-[#FF3B30]', 
        bg: 'bg-[#FF3B30]/20',
        category: 'auth'
      },
      'MFA_SETUP_INITIATED': { 
        label: 'MFA Setup Started', 
        icon: Shield, 
        color: 'text-[#2BBBAD]', 
        bg: 'bg-[#2BBBAD]/20',
        category: 'mfa'
      },
      'MFA_SETUP_COMPLETED': { 
        label: 'MFA Enabled', 
        icon: Shield, 
        color: 'text-[#10B981]', 
        bg: 'bg-[#10B981]/20',
        category: 'mfa'
      },
      'MFA_DISABLED': { 
        label: 'MFA Disabled', 
        icon: Shield, 
        color: 'text-[#FFCC00]', 
        bg: 'bg-[#FFCC00]/20',
        category: 'mfa'
      },
      'MFA_VERIFICATION_FAILED': { 
        label: 'MFA Failed', 
        icon: AlertTriangle, 
        color: 'text-[#FF3B30]', 
        bg: 'bg-[#FF3B30]/20',
        category: 'mfa'
      },
      'PANIC_ACTIVATED': { 
        label: 'Panic Activated', 
        icon: AlertTriangle, 
        color: 'text-[#FF3B30]', 
        bg: 'bg-[#FF3B30]/20',
        category: 'panic'
      },
      'PARTNER_PROTECTION_TOGGLED': { 
        label: 'Protection Changed', 
        icon: Shield, 
        color: 'text-[#2BBBAD]', 
        bg: 'bg-[#2BBBAD]/20',
        category: 'partner'
      },
      'PARTNER_UNLOCKED': { 
        label: 'Partner Unlocked', 
        icon: Unlock, 
        color: 'text-[#10B981]', 
        bg: 'bg-[#10B981]/20',
        category: 'recovery'
      },
      'RECOVERY_INITIATED': { 
        label: 'Recovery Started', 
        icon: Lock, 
        color: 'text-[#FFCC00]', 
        bg: 'bg-[#FFCC00]/20',
        category: 'recovery'
      },
      'RECOVERY_KYC_VERIFIED': { 
        label: 'KYC Verified', 
        icon: User, 
        color: 'text-[#10B981]', 
        bg: 'bg-[#10B981]/20',
        category: 'recovery'
      },
      'KYC_VERIFIED': { label: 'Identity Verified', icon: User, color: 'text-[#10B981]', bg: 'bg-[#10B981]/20', category: 'recovery' },
      'KYC_FAILED': { label: 'Identity Check Failed', icon: AlertTriangle, color: 'text-[#FF3B30]', bg: 'bg-[#FF3B30]/20', category: 'recovery' },
      'KYC_IDENTITY_MISMATCH': { label: 'ID Did Not Match', icon: AlertTriangle, color: 'text-[#FF3B30]', bg: 'bg-[#FF3B30]/20', category: 'recovery' },
      'RECOVERY_KYC_FAILED': { 
        label: 'KYC Failed', 
        icon: AlertTriangle, 
        color: 'text-[#FF3B30]', 
        bg: 'bg-[#FF3B30]/20',
        category: 'recovery'
      },
      'RECOVERY_UNLOCK_FAILED': { 
        label: 'Unlock Failed', 
        icon: AlertTriangle, 
        color: 'text-[#FF3B30]', 
        bg: 'bg-[#FF3B30]/20',
        category: 'recovery'
      },
      'RECOVERY_COMPLETED': { 
        label: 'Recovery Complete', 
        icon: Shield, 
        color: 'text-[#10B981]', 
        bg: 'bg-[#10B981]/20',
        category: 'recovery'
      },
    };
    return configs[action] || { 
      label: action, 
      icon: Clock, 
      color: 'text-[#8892B0]', 
      bg: 'bg-[#8892B0]/20',
      category: 'other'
    };
  };

  const filteredLogs = filter === 'all' 
    ? logs 
    : logs.filter(log => getActionConfig(log.action).category === filter);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-12 h-12"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="audit-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-black text-[#E6F1FF]">Audit Log</h1>
            <p className="text-[#8892B0] mt-1">Track all security events and activities</p>
          </div>

          <div className="flex items-center gap-3">
            <Filter size={20} className="text-[#8892B0]" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px] bg-[#112240] border-[#2BBBAD]/20 text-[#E6F1FF]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent className="bg-[#112240] border-[#2BBBAD]/20">
                <SelectItem value="all" className="text-[#E6F1FF]">All Events</SelectItem>
                <SelectItem value="auth" className="text-[#E6F1FF]">Authentication</SelectItem>
                <SelectItem value="mfa" className="text-[#E6F1FF]">MFA</SelectItem>
                <SelectItem value="panic" className="text-[#E6F1FF]">Panic Events</SelectItem>
                <SelectItem value="recovery" className="text-[#E6F1FF]">Recovery</SelectItem>
                <SelectItem value="partner" className="text-[#E6F1FF]">Partner Changes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="stat-card">
            <p className="text-2xl font-black text-[#E6F1FF]">{logs.length}</p>
            <p className="text-sm text-[#8892B0]">Total Events</p>
          </div>
          <div className="stat-card">
            <p className="text-2xl font-black text-[#FF3B30]">
              {logs.filter(l => l.action === 'PANIC_ACTIVATED').length}
            </p>
            <p className="text-sm text-[#8892B0]">Panic Activations</p>
          </div>
          <div className="stat-card">
            <p className="text-2xl font-black text-[#10B981]">
              {logs.filter(l => l.action === 'RECOVERY_COMPLETED').length}
            </p>
            <p className="text-sm text-[#8892B0]">Recoveries</p>
          </div>
          <div className="stat-card">
            <p className="text-2xl font-black text-[#FFCC00]">
              {logs.filter(l => l.action.includes('FAILED')).length}
            </p>
            <p className="text-sm text-[#8892B0]">Failed Attempts</p>
          </div>
        </div>

        {/* Timeline */}
        <div className="glass-card rounded-2xl p-6">
          {filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <Clock size={48} className="mx-auto text-[#8892B0] mb-4" />
              <p className="text-[#8892B0]">No events found</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredLogs.map((log, index) => {
                const config = getActionConfig(log.action);
                const Icon = config.icon;
                
                return (
                  <div 
                    key={log.id}
                    className="timeline-item animate-fade-in"
                    style={{ animationDelay: `${index * 30}ms` }}
                    data-testid={`audit-log-${index}`}
                  >
                    <div className={`timeline-dot ${log.action.includes('PANIC') ? 'panic' : log.action.includes('RECOVERY') ? 'recovery' : ''}`} />
                    <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-[#1A2F4B]/30 transition-colors">
                      <div className={`w-10 h-10 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon size={18} className={config.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className={`font-semibold ${config.color}`}>{config.label}</p>
                            {log.details && Object.keys(log.details).length > 0 && (
                              <p className="text-sm text-[#8892B0] mt-1">
                                {JSON.stringify(log.details)}
                              </p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-[#8892B0] font-mono">{formatTime(log.timestamp)}</p>
                            {log.ip_address && (
                              <p className="text-xs text-[#4A5568] mt-1">IP: {log.ip_address}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
